import React from 'react';
import { FormattedMessage } from 'react-intl';

import { FullScreenSvg } from '@assets';
import { IImage } from '@share/common-types';

import { MobileSlider } from '../../common/mobile-slider';
import { HotelImagesSlider } from '../hotel-image-slider';
import { QuintessPhotoGrid } from '../quintess-photo-grid';

import './style.scss';

interface IProps {
  images: IImage[];
  hotelName: string;
}

const HERO_THUMB_COUNT = 4;
const PHOTO_GRID_THRESHOLD = 10;

export const QuintessGallery: React.FunctionComponent<IProps> = ({
  images,
  hotelName,
}: IProps) => {
  const [isSliderVisible, setIsSliderVisible] = React.useState<boolean>(false);
  const [isGridVisible, setIsGridVisible] = React.useState<boolean>(false);
  const [startIndex, setStartIndex] = React.useState<number>(0);

  if (!images.length) {
    return null;
  }

  const heroImage = images[0];
  const thumbnails = images.slice(1, HERO_THUMB_COUNT + 1);
  const hasPhotoGrid = images.length > PHOTO_GRID_THRESHOLD;

  const openSlider = (index: number): void => {
    setStartIndex(index);
    setIsSliderVisible(true);
  };

  const handleGridSelect = (index: number): void => {
    setIsGridVisible(false);
    openSlider(index);
  };

  const renderOverlay = (index: number): React.ReactNode => (
    <div className="quintess-gallery__overlay" onClick={() => openSlider(index)}>
      <span className="quintess-gallery__overlay-button">
        <FullScreenSvg />
        <span className="quintess-gallery__overlay-text">
          <FormattedMessage id="full.view" />
        </span>
      </span>
    </div>
  );

  return (
    <div className="quintess-gallery">
      <div className="quintess-gallery__mosaic">
        <div className="quintess-gallery__hero">
          <img
            className="quintess-gallery__image"
            src={heroImage.url}
            alt={heroImage.description || hotelName}
          />
          {renderOverlay(0)}
        </div>

        {!!thumbnails.length && (
          <div className="quintess-gallery__thumbnails">
            {thumbnails.map((image, index) => (
              <div className="quintess-gallery__thumbnail" key={`${image.url}-${index}`}>
                <img
                  className="quintess-gallery__image"
                  src={image.url}
                  alt={image.description || hotelName}
                  loading="lazy"
                />
                {renderOverlay(index + 1)}
              </div>
            ))}
          </div>
        )}

        {!!hasPhotoGrid && (
          <button
            type="button"
            className="quintess-gallery__show-all"
            onClick={() => setIsGridVisible(true)}
          >
            <FormattedMessage id="show.all.photos" values={{ count: images.length }} />
          </button>
        )}
      </div>

      <div className="quintess-gallery__mobile">
        <MobileSlider images={images} />
        {!!hasPhotoGrid && (
          <button
            type="button"
            className="quintess-gallery__show-all mobile"
            onClick={() => setIsGridVisible(true)}
          >
            <FormattedMessage id="show.all.photos" values={{ count: images.length }} />
          </button>
        )}
      </div>

      <QuintessPhotoGrid
        images={images}
        visible={isGridVisible}
        hotelName={hotelName}
        onCancel={() => setIsGridVisible(false)}
        onSelect={handleGridSelect}
      />

      <HotelImagesSlider
        images={images}
        visible={isSliderVisible}
        hotelName={hotelName}
        startIndex={startIndex}
        onCancel={() => setIsSliderVisible(false)}
      />
    </div>
  );
};
